import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/navigation/NavigationTabBar';
import api from '../api/axiosInstance';

// Reusable QuestionCard Component
const QuestionCard = ({ question }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  
  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const toggleAnswer = () => {
    setShowAnswer(!showAnswer);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 hover:border-blue-300">
      {/* Header with Company and Difficulty */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-14m14 0V7a2 2 0 00-2-2H7a2 2 0 00-2 2v14" />
            </svg>
            <span className="font-semibold text-gray-900">{question.Company}</span>
          </div>
        </div>
        
        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getDifficultyColor(question.DiffType)}`}>
          {question.DiffType}
        </span>
      </div>

      {/* Question Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-4 hover:text-blue-600 transition-colors">
        {question.Question}
      </h3>

      {/* Tech Stack Badge */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-md border border-purple-200">
          {question.TechStack}
        </span>
      </div>

      {/* Expandable Answer Section */}
      {showAnswer && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg animate-in slide-in-from-top duration-200">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-green-800">Answer:</span>
          </div>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {question.Answer}
          </p>
        </div>
      )}

      {/* Footer with Date and View Answer Button */}
      <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {new Date(question.createdAt).toLocaleDateString()}
          </span>
        </div>
        
        <button
          onClick={toggleAnswer}
          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          {showAnswer ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Hide Answer
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              View Answer
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const CompanyQuestions = () => {
  const navigate = useNavigate();
  
  // Loading state
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTechStack, setSelectedTechStack] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch questions from API
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/companyquestions');
      console.log('Company questions response:', response.data);
      
      // Handle different response formats
      let questionsData = response.data;
      if (response.data && typeof response.data === 'object' && response.data.data) {
        questionsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        questionsData = response.data;
      }
      
      setQuestions(Array.isArray(questionsData) ? questionsData : []);
    } catch (error) {
      console.error('Error fetching company questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch questions on component mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  // Get unique companies, tech stacks, and difficulties from questions
  const companies = [...new Set(questions.map(q => q.Company).filter(Boolean))];
  const techStacks = [...new Set(questions.map(q => q.TechStack).filter(Boolean))];
  const difficulties = ['Easy', 'Medium', 'Hard'];

  // Filter questions based on all filters
  const filteredQuestions = questions.filter(question => {
    const matchesCompany = !selectedCompany || question.Company === selectedCompany;
    const matchesTechStack = !selectedTechStack || question.TechStack === selectedTechStack;
    const matchesDifficulty = !selectedDifficulty || question.DiffType === selectedDifficulty;
    const matchesSearch = !searchTerm || 
      question.Question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.Company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.TechStack.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCompany && matchesTechStack && matchesDifficulty && matchesSearch;
  });

  // Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-transparent border-t-blue-400 rounded-full animate-pulse"></div>
        </div>
      </div>
      <p className="mt-4 text-gray-600 font-medium">Loading questions...</p>
    </div>
  );

  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-[60px]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* PAGE TITLE */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Company Questions</h1>
            <p className="text-lg text-gray-600">
              Browse interview questions from top tech companies
            </p>
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Company Filter Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Company
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Companies</option>
                {companies.map(company => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>

            {/* Tech Stack Filter Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Tech Stack
              </label>
              <select
                value={selectedTechStack}
                onChange={(e) => setSelectedTechStack(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Tech Stacks</option>
                {techStacks.map(tech => (
                  <option key={tech} value={tech}>
                    {tech}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Difficulty
              </label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Difficulties</option>
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Questions
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search questions..."
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(selectedCompany || selectedTechStack || selectedDifficulty || searchTerm) && (
            <div className="mb-6">
              <button
                onClick={() => {
                  setSelectedCompany('');
                  setSelectedTechStack('');
                  setSelectedDifficulty('');
                  setSearchTerm('');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          )}

          {/* QUESTIONS LIST */}
          <div className="space-y-4">
            {loading ? (
              <LoadingSpinner />
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No questions found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your filters or search terms to find company questions
                </p>
              </div>
            ) : (
              filteredQuestions.map(question => (
                <QuestionCard key={question.id} question={question} />
              ))
            )}
          </div>

          {/* RESULTS COUNT */}
          {filteredQuestions.length > 0 && (
            <div className="mt-8 text-center text-sm text-gray-600">
              Showing {filteredQuestions.length} of {questions.length} questions
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CompanyQuestions;
